import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import set from 'lodash/set';
import isNil from 'lodash/isNil';
import reduce from 'lodash/reduce';
import filter from 'lodash/filter';
import isUndefined from 'lodash/isUndefined';
import moment from 'moment';
import 'moment-timezone';

import {
  TextField,
  Typography,
  Button,
  Stack,
  Grid,
  Paper,
  FormControl,
} from '@mui/material';

import MUIDataTable from 'mui-datatables';
import { DeploymentProgressPageStrings } from '../../common/strings';
import DeploymentProgressChips from './deployment-progress-page-chips';
import WrappedSpinner from '../../common/wrapped-spinner';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import marketActions from '../../../actions/markets';
import reportActions from '../../../actions/reports';
import {
  getMomentMillis,
  longDateTimeFormat,
  VEHICLE_EVENT_DROP_OFF as DROP_OFF,
  VEHICLE_EVENT_PICK_UP as PICK_UP,
  VEHICLE_EVENT_VEHICLE_MISSING as VEHICLE_MISSING,
  VEHICLE_EVENT_VEHICLE_INACCESSIBLE as VEHICLE_INACCESSIBLE,
} from '../../../lib/utils';
import { READ, MARKET_PAGE, REPORTS_PAGE } from '../../../access';
import { MarketSelect } from '../../common/market-select';
import saveLocalSettingsWrapper from './save-report-settings';
import BatteryLevel from '../../common/battery-level';

const LOCAL_STORAGE_KEY = 'deployment-progress-page';
const styles = {
  root: {
    width: '95%',
    height: '85%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
  formControl: {
    width: '100%',
    left: '10px',
  },
};

export function getTaskDisplay(taskHumanId, user, history, marketId) {
  if (!user) return;
  if (user.access.can(READ, MARKET_PAGE) && user.access.can(READ, REPORTS_PAGE)) {
    return (
      <Button
        color="primary"
        onClick={() => history.push(`/operator/markets/${marketId}/tasks/${taskHumanId}`)}
      >
        {taskHumanId}
      </Button>
    );
  } else {
    return taskHumanId;
  }
}

function filterEvents(
  events,
  filterDropOff,
  filterPickUp,
  filterVehicleMissing,
  filterVehicleInaccessible,
) {
  return filter(events, (event) => {
    if (filterDropOff && event.reason === DROP_OFF) return true;
    if (filterPickUp && event.reason === PICK_UP) return true;
    if (filterVehicleMissing && event.reason === VEHICLE_MISSING) return true;
    if (filterVehicleInaccessible && event.reason === VEHICLE_INACCESSIBLE) {
      return true;
    }
    return false;
  });
}

function getEventCounts(events) {
  return reduce(
    events,
    (accum, event) => {
      if (event.reason === PICK_UP) {
        set(accum, 'totalPickUp', accum.totalPickUp + 1);
      } else if (event.reason === DROP_OFF) {
        set(accum, 'totalDropOff', accum.totalDropOff + 1);
      } else if (event.reason === VEHICLE_MISSING) {
        set(accum, 'totalVehicleMissing', accum.totalVehicleMissing + 1);
      } else if (event.reason === VEHICLE_INACCESSIBLE) {
        set(accum, 'totalVehicleInaccessible', accum.totalVehicleInaccessible + 1);
      }
      set(accum, 'total', accum.total + 1);
      return accum;
    },
    {
      total: 0,
      totalPickUp: 0,
      totalDropOff: 0,
      totalVehicleMissing: 0,
      totalVehicleInaccessible: 0,
    },
  );
}

function maintenanceFieldRenderer(booleanFlag) {
  let renderString = '';
  if (booleanFlag === true) {
    renderString = 'Maintenance';
  }
  return renderString;
}

function showVehicleStatus(statusList) {
  if (statusList) {
    return (
      <Grid>
        {statusList.map(
          (status) => status && <Typography key={status}>{status}</Typography>,
        )}
      </Grid>
    );
  } else {
    return '';
  }
}

function getVehicleStatus(event) {
  const statuses = [];
  statuses.push(event.vehicleExtStatus);
  statuses.push(event.vehicleExtDeploymentStatus);
  statuses.push(event.vehicleExtPossessionStatus);

  return statuses;
}

function getPreviousVehicleStatus(event) {
  const statuses = [];
  statuses.push(event.vehiclePreviousExtStatus);
  statuses.push(event.vehiclePreviousExtDeploymentStatus);
  statuses.push(event.vehiclePreviousExtPossessionStatus);

  return statuses;
}
class DeploymentProgressPage extends React.Component {
  constructor(props) {
    super(props);

    const {
      marketId,
      filterPickUp,
      filterDropOff,
      filterVehicleMissing,
      filterVehicleInaccessible,
      startDate,
      endDate,
    } = props;

    this.state = {
      startDate: startDate
        ? moment.utc(startDate).format('yyyy-MM-DDTHH:mm')
        : moment.utc().format('yyyy-MM-DDT00:00'),
      endDate: endDate
        ? moment.utc(endDate).format('yyyy-MM-DDTHH:mm')
        : moment.utc().format('yyyy-MM-DDT23:59'),
      marketId: marketId || '',
      filterPickUp: isNil(filterPickUp) ? true : filterPickUp,
      filterDropOff: isNil(filterDropOff) ? true : filterDropOff,
      filterVehicleMissing: isNil(filterVehicleMissing)
        ? true
        : filterVehicleMissing,
      filterVehicleInaccessible: isNil(filterVehicleInaccessible)
        ? true
        : filterVehicleInaccessible,
    };
    this.handleChange = this.handleChange.bind(this);
    this.formatTableData = this.formatTableData.bind(this);
    this.handleFilterClick = this.handleFilterClick.bind(this);
    this.saveSettings = this.saveSettings.bind(this);
    this.fetchData = this.fetchData.bind(this);
  }

  componentDidMount() {
    const { markets, fetchActiveMarkets, marketId } = this.props;
    if (!markets.activeMarkets) fetchActiveMarkets();
    if (marketId) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      marketId, startDate, endDate,
    } = this.state;
    if (isUndefined(startDate) || isUndefined(endDate)) return;
    const isMarketChanged = marketId !== prevState.marketId;
    const isSameStartDate = moment(prevState.startDate).isSame(startDate);
    const isSameEndDate = moment(prevState.endDate).isSame(endDate);
    const isDateChanged = !isSameStartDate || !isSameEndDate;

    if (isMarketChanged || isDateChanged) {
      this.saveSettings();

      if (isMarketChanged && marketId && marketId.length > 0) {
        this.fetchData();
      }
    }
  }

  get tableOptions() {
    const { tableOptions: basicOptions } = this.props;

    basicOptions.downloadOptions = {
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      },
    };

    return basicOptions;
  }

  get columns() {
    const { marketId } = this.state;
    const {
      addToColumnData,
      selectedFilters,
      primarySort,
      history,
      currentWrangler: user,
    } = this.props;
    const columnData = [
      { name: 'Vehicle ID' },
      { name: 'Reason' },
      { name: 'Previous State/Status', options: { customBodyRender: (previousStatusList) => showVehicleStatus(previousStatusList) } },
      { name: 'State/Status', options: { customBodyRender: (statusList) => showVehicleStatus(statusList) } },
      { name: 'Wrangler' },
      {
        name: 'Timestamp',
        options: {
          customBodyRender: (data) => moment.utc(data.date).tz(data.tz).format(longDateTimeFormat),
          sortCompare: (order) => (obj1, obj2) => (obj1.data.date.localeCompare(obj2.data.date)) * (order === 'asc' ? 1 : -1),
        },
      },
      { name: 'Battery Level', options: { customBodyRender: (batteryLevel) => (batteryLevel ? <BatteryLevel batteryLevel={batteryLevel} /> : <></>) } },
      { name: 'Maintenance', options: { customBodyRender: (booleanFlag) => maintenanceFieldRenderer(booleanFlag) } },
      { name: 'Hub Location' },
      { name: 'Geolocation' },
      { name: 'Task', options: { customBodyRender: (taskHumanId) => getTaskDisplay(taskHumanId, user, history, marketId) } },
    ];
    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  fetchData() {
    const { fetchDeploymentEvents } = this.props;
    const { marketId, startDate, endDate } = this.state;
    const query = {
      market_id: marketId,
      dateFrom: getMomentMillis(startDate),
      dateTo: getMomentMillis(endDate),
    };

    fetchDeploymentEvents(query);
  }

  saveSettings() {
    const { saveReportSettings } = this.props;
    const {
      marketId,
      filterPickUp,
      filterDropOff,
      filterVehicleMissing,
      filterVehicleInaccessible,
      startDate,
      endDate,
    } = this.state;
    const savedSettings = {
      startDate,
      endDate,
      marketId,
      filterPickUp,
      filterDropOff,
      filterVehicleMissing,
      filterVehicleInaccessible,
    };
    saveReportSettings(savedSettings);
  }

  handleChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  handleFilterClick(label) {
    const { state } = this;
    const newState = {};
    newState[label] = !state[label];

    this.setState(newState, () => this.saveSettings());
  }

  // eslint-disable-next-line class-methods-use-this
  formatTableData(events) {
    return events.map((event) => [
      event.vehicleHumanId,
      event.reason,
      getPreviousVehicleStatus(event),
      getVehicleStatus(event),
      `${get(event, 'wrangler.firstName', '')} ${get(event, 'wrangler.lastName', '')}`,
      { date: event.createdAt, tz: get(event, 'market.tz', 'America/New_York') },
      event.batteryLevel,
      event.vehicleNeedsMaintenance,
      `${get(event, 'hubLocation', '')}`,
      `${get(event, 'geo.geoJson.coordinates[0]', '')}, ${get(event, 'geo.geoJson.coordinates[1]', '')}`,
      event.taskHumanId,
    ]);
  }

  render() {
    const {
      markets, reports, getTitleFromData,
    } = this.props;
    const {
      marketId,
      filterPickUp,
      filterDropOff,
      filterVehicleMissing,
      filterVehicleInaccessible,
      startDate,
      endDate,
    } = this.state;
    const isLoading = markets.isLoading || reports.isLoading;

    /*
     get state by query data to avoid view re-renders which reset component state
     those re-renders would clear out input fields like date & market but still load the last shown report
    */
    const deploymentEvents = get(
      reports,
      `deploymentEvents.${marketId}${getMomentMillis(startDate)}${getMomentMillis(endDate)}`,
      [],
    );

    const eventCounts = getEventCounts(deploymentEvents);

    const filteredEvents = filterEvents(
      deploymentEvents,
      filterDropOff,
      filterPickUp,
      filterVehicleMissing,
      filterVehicleInaccessible,
    );

    const eventTableData = this.formatTableData(filteredEvents);

    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ mt: 3, pt: 1 }}>
          <Grid container>
            <Grid item xs={3}>
              <MarketSelect
                value={marketId}
                handleChange={(newMarketId) => this.setState({ marketId: newMarketId })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl
                component="fieldset"
                style={styles.formControl}
              >
                <Stack direction="row" spacing={1} component="form">
                  <TextField
                    label="Date From"
                    type="datetime-local"
                    name="startDate"
                    value={startDate}
                    sx={{ width: 200 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={this.handleChange}
                  />
                  <TextField
                    label="Date To"
                    type="datetime-local"
                    value={endDate}
                    name="endDate"
                    sx={{ ml: 3, width: 200 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={this.handleChange}
                  />
                  {!isLoading && (
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ ml: 3 }}
                      onClick={this.fetchData}
                    >
                      {DeploymentProgressPageStrings.reload}
                    </Button>
                  )}
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <DeploymentProgressChips
                counts={eventCounts}
                pickUpSelected={filterPickUp}
                dropOffSelected={filterDropOff}
                vehicleMissingSelected={filterVehicleMissing}
                vehicleInaccessibleSelected={filterVehicleInaccessible}
                handleClick={this.handleFilterClick}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              {deploymentEvents && marketId && startDate && endDate && (
                <MUIDataTable
                  title={getTitleFromData(
                    'Deployment Progress Report',
                    eventTableData,
                  )}
                  columns={this.columns}
                  data={eventTableData}
                  options={this.tableOptions}
                />
              )}
            </Grid>
          </Grid>
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...marketActions,
  ...reportActions,
}, dispatch);

const mapStateToProps = ({ markets, reports, wranglers }) => ({
  markets,
  reports,
  currentWrangler: wranglers.currentWrangler,
});

export default saveLocalSettingsWrapper(
  withPersistentSettings(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(withRouter(DeploymentProgressPage)),
    LOCAL_STORAGE_KEY,
  ),
  LOCAL_STORAGE_KEY,
);
